import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  hideCancel?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};

// Imperative API for non-React code (services, contexts not under a component).
let imperativeConfirm: ConfirmFn | null = null;
export const confirmAction: ConfirmFn = (opts) => {
  if (!imperativeConfirm) {
    return Promise.resolve(window.confirm(typeof opts.message === 'string' ? opts.message : opts.title || 'Confirm'));
  }
  return imperativeConfirm(opts);
};

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  useEffect(() => {
    imperativeConfirm = confirm;
    return () => {
      imperativeConfirm = null;
    };
  }, [confirm]);

  const close = useCallback(
    (value: boolean) => {
      if (pending) {
        pending.resolve(value);
        setPending(null);
      }
    },
    [pending]
  );

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    confirmBtnRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div className="confirm-overlay" onClick={() => close(false)}>
          <div
            className={`confirm-dialog ${pending.danger ? 'confirm-danger' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {pending.title && <h2 className="confirm-title">{pending.title}</h2>}
            <div className="confirm-message">{pending.message}</div>
            <div className="confirm-actions">
              {!pending.hideCancel && (
                <button className="confirm-btn confirm-btn-cancel" onClick={() => close(false)}>
                  {pending.cancelLabel || 'Cancel'}
                </button>
              )}
              <button
                ref={confirmBtnRef}
                className={`confirm-btn ${pending.danger ? 'confirm-btn-danger' : 'confirm-btn-primary'}`}
                onClick={() => close(true)}
              >
                {pending.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};
